/**
 * One way to make a write, for every heart, tick and star on the site.
 *
 * A write that the server refuses used to leave no trace: the calendar caught only the
 * sign-in case and re-threw the rest into a `void`, five pages patched the row first and
 * dropped the request's promise, and the briefing put up a `window.alert`. On the first two
 * a failed press was indistinguishable from a dead control — `/favourite` answered 400 on
 * every press for weeks and was diagnosed as CSS three times.
 *
 * `attempt` runs the write and answers whether it took. A 401 sends the reader to sign in
 * and back to where they were; anything else is written into a sentence that `WriteTrouble`
 * shows, and a later success clears it — the thing works again, and a stale warning is its
 * own kind of lie.
 */
import { ApiError, NeedsSignIn } from './client'

let trouble: string | null = null
const listeners = new Set<() => void>()

function set(next: string | null): void {
  if (next === trouble) return
  trouble = next
  for (const tell of listeners) tell()
}

/** The last write failure as a sentence for a reader, or `null` while everything is fine. */
export function writeTrouble(): string | null {
  return trouble
}

export function subscribeWriteTrouble(tell: () => void): () => void {
  listeners.add(tell)
  return () => {
    listeners.delete(tell)
  }
}

export function clearWriteTrouble(): void {
  set(null)
}

/**
 * In the reader's terms, with the status kept so the editor can tell our bug from the box's.
 * `ApiError.detail` is what the server said; it is left out here on purpose — a 400's detail
 * names a field the reader never typed, and a gateway's names a proxy they have never heard of.
 */
function describe(error: unknown): string {
  if (error instanceof ApiError) return `That didn't save — the server answered ${error.status}.`
  // `fetch` rejects with a TypeError when there is no network at all.
  if (error instanceof TypeError) return "That didn't save — the site couldn't be reached."
  return `That didn't save — ${error instanceof Error ? error.message : String(error)}`
}

/** Run a write. `true` when it took; otherwise the reader has been told, or sent to sign in. */
export async function attempt(work: () => Promise<unknown>): Promise<boolean> {
  try {
    await work()
  } catch (error: unknown) {
    if (error instanceof NeedsSignIn) {
      window.location.href = `/login?next=${encodeURIComponent(error.next)}`
      return false
    }
    set(describe(error))
    return false
  }
  set(null)
  return true
}
